import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CalendarIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalculatorIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import ExportButtons from '../../components/ExportButtons';
import DateQuickPresets from '../../components/DateQuickPresets';

const DRE = () => {
  const [dados, setDados] = useState({
    periodo: { inicio: '', fim: '' },
    totais: {
      receitas: 0,
      despesas: 0,
      resultado: 0,
      margem: 0
    },
    detalhes: [],
    estrutura: []
  });
  
  const [filtros, setFiltros] = useState({
    data_inicio: format(new Date().setMonth(new Date().getMonth() - 1), 'yyyy-MM-dd'),
    data_fim: format(new Date(), 'yyyy-MM-dd'),
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas'
  });
  
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [carregando, setCarregando] = useState(false);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);

  useEffect(() => {
    carregarDRE();
    carregarDropdowns();
  }, [filtros]);

  const carregarDRE = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/dre', { params: filtros });
      setDados(response.data);
    } catch (error) {
      console.error('Erro ao carregar DRE:', error);
      toast.error('Erro ao carregar DRE');
    } finally {
      setCarregando(false);
    }
  };

  const carregarDropdowns = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias')
      ]);
      
      setCentrosCustos(centrosResponse.data);
      setContasBancarias(contasResponse.data);
    } catch (error) {
      console.error('Erro ao carregar dropdowns:', error);
    }
  };

  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(valor);
  };

  const formatarPercentual = (valor) => {
    return `${valor.toFixed(2)}%`;
  };

  const limparFiltros = () => {
    setFiltros({
      data_inicio: format(new Date().setMonth(new Date().getMonth() - 1), 'yyyy-MM-dd'),
      data_fim: format(new Date(), 'yyyy-MM-dd'),
      centro_custo_id: 'todos',
      conta_bancaria_id: 'todas'
    });
  };

  const receitasPorGrupo = dados.detalhes
    .filter(d => d.tipo === 'receita')
    .reduce((acc, curr) => {
      const grupo = curr.grupo || 'Não Categorizado';
      if (!acc[grupo]) acc[grupo] = 0;
      acc[grupo] += curr.total;
      return acc;
    }, {});

  const despesasPorGrupo = dados.detalhes
    .filter(d => d.tipo === 'despesa')
    .reduce((acc, curr) => {
      const grupo = curr.grupo || 'Não Categorizado';
      if (!acc[grupo]) acc[grupo] = 0;
      acc[grupo] += curr.total;
      return acc;
    }, {});

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📊 Demonstração do Resultado do Exercício</h1>
          <p className="text-gray-600">Análise detalhada dos resultados da empresa</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            className="btn-secondary"
          >
            <FunnelIcon className="h-5 w-5 mr-2" />
            {mostrarFiltros ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </button>
          <ExportButtons tipo="dre" filtros={filtros} />
        </div>
      </div>

      {/* Filtros */}
      {mostrarFiltros && (
        <div className="card mb-6 animate-slide-in">
          <div className="card-body">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">🔍 Filtros do Relatório</h3>
              <button
                onClick={() => setMostrarFiltros(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <DateQuickPresets
              mode="range"
              startDate={filtros.data_inicio}
              endDate={filtros.data_fim}
              onSelect={({ startDate, endDate }) =>
                setFiltros((old) => ({ ...old, data_inicio: startDate, data_fim: endDate }))
              }
              className="mb-4"
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="form-group">
                <label className="form-label">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Data Início
                </label>
                <input
                  type="date"
                  value={filtros.data_inicio}
                  onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Data Fim
                </label>
                <input
                  type="date"
                  value={filtros.data_fim}
                  onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Centro de Custo</label>
                <select
                  value={filtros.centro_custo_id}
                  onChange={(e) => setFiltros({ ...filtros, centro_custo_id: e.target.value })}
                  className="form-select"
                >
                  <option value="todos">Todos os Centros</option>
                  {centrosCustos.map(centro => (
                    <option key={centro.id} value={centro.id}>
                      {centro.codigo} - {centro.descricao}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Conta Bancária</label>
                <select
                  value={filtros.conta_bancaria_id}
                  onChange={(e) => setFiltros({ ...filtros, conta_bancaria_id: e.target.value })}
                  className="form-select"
                >
                  <option value="todas">Todas as Contas</option>
                  {contasBancarias.map(conta => (
                    <option key={conta.id} value={conta.id}>
                      {conta.banco} - {conta.conta}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-4 flex justify-between">
              <button
                onClick={limparFiltros}
                className="btn-secondary"
              >
                Limpar Filtros
              </button>
              <div className="space-x-3">
                <button
                  onClick={() => setMostrarFiltros(false)}
                  className="btn-secondary"
                >
                  Fechar
                </button>
                <button
                  onClick={carregarDRE}
                  disabled={carregando}
                  className="btn-primary"
                >
                  {carregando ? 'Carregando...' : 'Atualizar DRE'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="card bg-success-50 border-success-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-success-100">
                <ArrowTrendingUpIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-success-600">Total Receitas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatarValor(dados.totais.receitas)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-danger-50 border-danger-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-danger-100">
                <ArrowTrendingDownIcon className="h-6 w-6 text-danger-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-danger-600">Total Despesas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatarValor(dados.totais.despesas)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-primary-50 border-primary-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <CalculatorIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-primary-600">Resultado</p>
                <p className={`text-2xl font-bold ${
                  dados.totais.resultado >= 0 ? 'text-success-600' : 'text-danger-600'
                }`}>
                  {formatarValor(dados.totais.resultado)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-warning-50 border-warning-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-warning-100">
                <ChartBarIcon className="h-6 w-6 text-warning-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-warning-600">Margem</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatarPercentual(dados.totais.margem)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DRE Detalhada */}
      <div className="card mb-6">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              📋 Demonstração do Resultado do Exercício
            </h3>
            <div className="text-sm text-gray-500">
              Período: {format(parseISO(dados.periodo.inicio || new Date().toISOString()), 'dd/MM/yyyy', { locale: ptBR })} 
              {' a '}
              {format(parseISO(dados.periodo.fim || new Date().toISOString()), 'dd/MM/yyyy', { locale: ptBR })}
            </div>
          </div>
        </div>
        
        <div className="card-body">
          {/* Receitas */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <ArrowTrendingUpIcon className="h-5 w-5 text-success-600 mr-2" />
              <h4 className="text-lg font-semibold text-success-700">RECEITAS</h4>
            </div>
            
            <div className="space-y-2">
              {Object.entries(receitasPorGrupo).map(([grupo, total]) => (
                <div key={grupo} className="flex justify-between items-center py-2 border-b border-gray-100">
                  <div className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-success-500 mr-3"></div>
                    <span>{grupo}</span>
                  </div>
                  <span className="font-medium text-success-600">
                    {formatarValor(total)}
                  </span>
                </div>
              ))}
              
              <div className="flex justify-between items-center py-3 font-bold text-success-700 border-t mt-2">
                <span>TOTAL RECEITAS</span>
                <span>{formatarValor(dados.totais.receitas)}</span>
              </div>
            </div>
          </div>

          {/* Despesas */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <ArrowTrendingDownIcon className="h-5 w-5 text-danger-600 mr-2" />
              <h4 className="text-lg font-semibold text-danger-700">DESPESAS</h4>
            </div>
            
            <div className="space-y-2">
              {Object.entries(despesasPorGrupo).map(([grupo, total]) => (
                <div key={grupo} className="flex justify-between items-center py-2 border-b border-gray-100">
                  <div className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-danger-500 mr-3"></div>
                    <span>{grupo}</span>
                  </div>
                  <span className="font-medium text-danger-600">
                    {formatarValor(total)}
                  </span>
                </div>
              ))}
              
              <div className="flex justify-between items-center py-3 font-bold text-danger-700 border-t mt-2">
                <span>TOTAL DESPESAS</span>
                <span>{formatarValor(dados.totais.despesas)}</span>
              </div>
            </div>
          </div>

          {/* Resultado */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <CalculatorIcon className="h-6 w-6 text-primary-600 mr-3" />
                <div>
                  <h4 className="text-xl font-bold text-gray-900">RESULTADO DO PERÍODO</h4>
                  <p className="text-sm text-gray-600">
                    Receitas - Despesas = Resultado
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-3xl font-bold ${
                  dados.totais.resultado >= 0 ? 'text-success-600' : 'text-danger-600'
                }`}>
                  {formatarValor(dados.totais.resultado)}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Margem: {formatarPercentual(dados.totais.margem)}
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-center">
                {dados.totais.resultado >= 0 ? (
                  <div className="flex items-center text-success-600">
                    <div className="w-3 h-3 rounded-full bg-success-500 mr-2"></div>
                    <span className="font-medium">✓ LUCRO NO PERÍODO</span>
                  </div>
                ) : (
                  <div className="flex items-center text-danger-600">
                    <div className="w-3 h-3 rounded-full bg-danger-500 mr-2"></div>
                    <span className="font-medium">✗ PREJUÍZO NO PERÍODO</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Estrutura DRE Aplicada */}
      {dados.estrutura.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">📐 Estrutura DRE Aplicada</h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {dados.estrutura.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="font-mono text-sm bg-white px-2 py-1 rounded mr-3">
                      {item.ordem}
                    </span>
                    <span>{item.nome}</span>
                  </div>
                  <span className={`text-sm font-medium ${
                    item.tipo === 'receita' ? 'text-success-600' :
                    item.tipo === 'despesa' ? 'text-danger-600' :
                    'text-primary-600'
                  }`}>
                    {item.tipo === 'receita' ? 'Receita' :
                     item.tipo === 'despesa' ? 'Despesa' : 'Resultado'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Análise */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">📈 Análise do Período</h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Relação Receitas/Despesas</p>
                <div className="mt-2">
                  <div className="flex h-4 rounded-full overflow-hidden">
                    <div 
                      className="bg-success-500"
                      style={{ 
                        width: `${dados.totais.receitas > 0 ? (dados.totais.receitas / (dados.totais.receitas + dados.totais.despesas) * 100) : 0}%` 
                      }}
                    ></div>
                    <div 
                      className="bg-danger-500"
                      style={{ 
                        width: `${dados.totais.despesas > 0 ? (dados.totais.despesas / (dados.totais.receitas + dados.totais.despesas) * 100) : 0}%` 
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Receitas: {formatarValor(dados.totais.receitas)}</span>
                    <span>Despesas: {formatarValor(dados.totais.despesas)}</span>
                  </div>
                </div>
              </div>
              
              <div className="pt-3 border-t border-gray-100">
                <p className="text-sm text-gray-600">Margem de Lucratividade</p>
                <div className="mt-2">
                  <div className="flex h-4 rounded-full overflow-hidden bg-gray-200">
                    <div 
                      className={`${dados.totais.margem >= 0 ? 'bg-success-500' : 'bg-danger-500'}`}
                      style={{ 
                        width: `${Math.min(Math.abs(dados.totais.margem), 100)}%` 
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Margem: {formatarPercentual(dados.totais.margem)}</span>
                    <span className={dados.totais.margem >= 0 ? 'text-success-600' : 'text-danger-600'}>
                      {dados.totais.margem >= 0 ? 'Lucrativo' : 'Prejuízo'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">💡 Recomendações</h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {dados.totais.margem < 10 ? (
                <div className="p-3 bg-warning-50 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-warning-500 mt-1.5"></div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-warning-800">
                        Atenção: Margem baixa
                      </p>
                      <p className="text-sm text-warning-700 mt-1">
                        Considere reduzir despesas ou aumentar receitas para melhorar a margem.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-success-50 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-success-500 mt-1.5"></div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-success-800">
                        Ótimo desempenho
                      </p>
                      <p className="text-sm text-success-700 mt-1">
                        A empresa está com boa margem de lucratividade. Continue com o bom trabalho!
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {dados.totais.despesas > dados.totais.receitas * 0.7 && (
                <div className="p-3 bg-danger-50 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-danger-500 mt-1.5"></div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-danger-800">
                        Despesas elevadas
                      </p>
                      <p className="text-sm text-danger-700 mt-1">
                        As despesas estão representando mais de 70% das receitas. Recomenda-se revisão de custos.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5"></div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-blue-800">
                      Próximos passos
                    </p>
                    <ul className="text-sm text-blue-700 mt-1 space-y-1">
                      <li>• Exporte o relatório para análise detalhada</li>
                      <li>• Compare com períodos anteriores</li>
                      <li>• Revise as principais categorias de despesas</li>
                      <li>• Planeje ações para o próximo período</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DRE;
